import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import type { SupportedLanguage } from '@dx-ui/framework-i18n';
import { RouterContext } from '../router-context';
import { DEFAULT_LOCALE } from '../types';
import { parsePathSegments } from '../utils/parse-path-segments';
import { extractRouteParamKeys } from '../utils/extract-route-param-keys';

export type NextPagesRouterProviderProps = {
  locale?: SupportedLanguage;
  defaultLocale?: SupportedLanguage;
  children: ReactNode;
};

/**
 * Adapter provider for Next.js Pages Router.
 * Reads locale, pathname, query, and params from `next/router` and provides
 * them as a `RouterContextValue`. Locale can be overridden by passing the
 * `locale` or `defaultLocale` props explicitly.
 */
export function NextPagesRouterProvider({
  locale: localeProp,
  defaultLocale: defaultLocaleProp,
  children,
}: NextPagesRouterProviderProps) {
  const router = useRouter();

  const value = useMemo(() => {
    const paramKeys = extractRouteParamKeys(router.pathname);
    const params: Record<string, string | string[] | undefined> = {};
    for (const key of paramKeys) {
      params[key] = router.query[key];
    }

    return {
      locale: (localeProp ?? router.locale ?? DEFAULT_LOCALE) as SupportedLanguage,
      defaultLocale: (defaultLocaleProp ??
        router.defaultLocale ??
        DEFAULT_LOCALE) as SupportedLanguage,
      pathname: router.pathname,
      pathSegments: parsePathSegments(router.pathname),
      asPath: router.asPath,
      isReady: router.isReady,
      query: router.query as Record<string, string | string[] | undefined>,
      params,
    };
  }, [
    router.locale,
    router.defaultLocale,
    router.pathname,
    router.query,
    router.asPath,
    router.isReady,
    localeProp,
    defaultLocaleProp,
  ]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}
